import {Injectable} from '@angular/core';
import mixpanel from 'mixpanel-browser';
import {environment} from '@env/environment';
import {OrgTypesList, RoleType, RoleTypeFriendlyName, User} from '@app/core/model/user';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {Subject} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {LogService} from '@app/core/log/log.service';

@Injectable({
    providedIn: 'root'
})
export class MixpanelService {

    private mixpanel: any;
    private groupData: any = {};
    private pageviewExtraData: any = {};
    private readonly origin = 'Connect';
    private debounceSubject: any = {};

    constructor(public authenticatedUserService: AuthenticatedUserService,
                private logService: LogService
    ) {
        if (environment.mixpanelToken) {
            mixpanel.init(environment.mixpanelToken, {
                debug: !environment.production,
                persistence: 'localStorage'
            });

            this.mixpanel = mixpanel;
            this.mixpanel.register({origin: this.origin});
        }
    }

    identify(user: User): void {
        if (!this.mixpanel) {
            return;
        }

        this.mixpanel.identify(user.guid);
        try {
            this.setGroupData(user);
        } catch (error) {
            this.logService.logError(error?.message);
        }
    }

    setPeopleData(user: User): void {
        if (!this.mixpanel) {
            return;
        }

        const isTestAccount = (
            (user?.email || '').toLowerCase().includes('test')
            || (user?.orgName || '').toLowerCase().includes('test'));
        const role = this.getUserRole(user);

        this.mixpanel.people.set({
            $name: `${user.orgName} - ${role} - ${user.guid}`,
            testAccount: isTestAccount,
            role: role,
            orgId: user.orgGuid,
            orgName: user.orgName,
            orgType: OrgTypesList[user.orgType]
        });
    }

    setGroupData(user: User): void {
        if (!this.mixpanel) {
            return;
        }

        const role = this.getUserRole(user);
        const orgType = OrgTypesList[user.orgType];

        // organization group
        this.mixpanel.add_group('organization', user.orgGuid);
        this.mixpanel.set_group('organization', user.orgGuid);
        const group = this.mixpanel.get_group('organization', user.orgGuid);
        if (group) {
            group.set({'name': user.orgName});
        }

        // orgType group
        this.mixpanel.add_group('orgType', orgType);
        this.mixpanel.set_group('orgType', orgType);

        // role group
        this.mixpanel.add_group('role', role);
        this.mixpanel.set_group('role', role);

        this.groupData = {
            'organization': user.orgGuid,
            'orgType': orgType,
            'role': role
        };

        this.mixpanel.register({
            origin: this.origin,
            ...this.groupData
        });
    }

    track(eventName: string, properties: any = {}): void {
        if (!this.mixpanel) {
            return;
        }

        this.mixpanel.track(eventName, properties);
    }

    setPageviewExtraData(pageviewExtraData: any = {}): void {
        this.pageviewExtraData = pageviewExtraData;
    }

    trackPageview(pageTitle: string, path: string, extraData: any = {}): void {
        if (!this.mixpanel) {
            return;
        }

        this.mixpanel.track_pageview({
            title: pageTitle,
            current_page_title: pageTitle,
            path: path,
            current_url_path: path,
            ...this.pageviewExtraData,
            ...extraData
        });
        this.pageviewExtraData = {};
    }

    reset(): void {
        if (!this.mixpanel) {
            return;
        }

        this.groupData = {};
        this.mixpanel.reset();
    }

    trackLogin(user: User): void {
        const method = (user?.etxOrgId ? 'Internal' : 'Username and Password');
        this.track('Login', {
            method: method,
            role: this.getUserRole(user),
            orgId: user.orgGuid,
            orgName: user.orgName
        });
    }

    trackLogout(): void {
        this.track('Logout');
    }

    trackSSO(url: string, resourceName: string, user: User): void {
        this.track('SSO', {
            url: url,
            'resource Id': resourceName,
            'resource name': resourceName,
            'resource type': 'BKM',
            appId: resourceName,
            role: this.getUserRole(user),
            orgId: user.orgGuid,
            orgName: user.orgName
        });
    }

    trackClickActivateApp(application: string, isDataSource: boolean, isAutoProvision: boolean): void {
        this.track('ClickActivateApp', {
            application: application,
            isDataSource: isDataSource,
            isAutoProvision: isAutoProvision
        });
    }

    trackDataSchema(dataSchema: string, dname: string): void {
        this.track('CreateDataSchema', {
            dataSchema: dataSchema,
            dname: dname
        });
    }

    trackCreateDataSource(applicationName: string, application: string, applicatioGuid: string): void {
        this.track('CreateDataSource', {
            applicationName: applicationName,
            application: application,
            applicatioGuid: applicatioGuid
        });
    }

    trackCreateApplication(applicationName: string, application: string, applicatioGuid: string): void {
        this.track('CreateApplication', {
            applicationName: applicationName,
            application: application,
            applicatioGuid: applicatioGuid
        });
    }

    trackActivateApp(properties: any = {}): void {
        this.track('ActivateApp', properties);
    }

    trackAppChangeRequest(properties: any = {}): void {
        this.track('AppChangeRequest', properties);
    }

    trackAppApproveRequest(properties: any = {}): void {
        this.track('AppApproveRequest', properties);
    }

    trackAppRejectRequest(properties: any = {}): void {
        this.track('AppRejectRequest', properties);
    }

    trackAppDiscardRequest(properties: any = {}): void {
        this.track('AppDiscardRequest', properties);
    }

    trackSavePassportLogin(numberOfIdps: number): void {
        this.track('SavePassportLogin', {numberOfAuthMethods: numberOfIdps});
    }

    trackFilterDataBrowsing(properties: any = {}): void {
        this.track('FilterDataBrowsing', properties);
    }

    trackGlobalSearch(query: string): void {
        this.track('GlobalSearch', {query: query});
    }

    trackFilterAppGallery(
        featureTypesCategories: string[],
        productCategories: string[],
        search: string,
        sortOption: string,
        numberOfApps: number
    ): void {
        this.debounceTrack('FilterAppGallery', {
            featureTypesCategories: featureTypesCategories,
            productCategories: productCategories,
            search: search,
            sortOption: sortOption,
            numberOfApps: numberOfApps
        });
    }

    private debounceTrack(eventName: string, properties: any = {}, time: number = 3000): void {
        if (!this.debounceSubject[eventName]) {
            this.debounceSubject[eventName] = new Subject();
            this.debounceSubject[eventName]
                .pipe(debounceTime(time))
                .subscribe((data: any) =>
                    this.track(eventName, data)
                );
        }

        this.debounceSubject[eventName].next(properties);
    }

    private getUserRole(user: User): string {
        if (!user) {
            return '';
        }

        if (user.role === RoleType.SYS_ADMIN) {
            return RoleTypeFriendlyName[user.role];
        }

        const isGG4LAccount = (user?.email || '').toLowerCase().endsWith('@gg4l.com');
        if (user.fullName === 'Service Operator'
            || user.fullName === 'GG4L Admin'
            || user.fullName === `${environment.customer.customerShortName} Admin`
            || isGG4LAccount
        ) {
            return 'Service Operator';
        }

        return RoleType[user.role] ? 'Admin' : '';
    }
}
